import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Cookies from 'js-cookie';

// Import only English translation by default
import enTranslation from './lang/en.json';

// Track loaded languages
const loadedLanguages = ['en'];

// Initialize i18next
i18n
    // Detect user language
    .use(LanguageDetector)
    // Pass the i18n instance to react-i18next.
    .use(initReactI18next)
    // Initialize i18next
    .init({
        debug: process.env.NODE_ENV === 'development',
        fallbackLng: 'en',
        interpolation: {
            escapeValue: false, // React already safes from XSS
        },
        resources: {
            en: {
                translation: enTranslation.en,
            },
        },
        // react-i18next compatibility options
        react: {
            useSuspense: false,
        },
    });

/**
 * Sets the i18n language
 * @param lang The language code
 * @returns The language code
 */
function setI18nLanguage(lang: string) {
    i18n.changeLanguage(lang);

    // Set cookie for server-side rendering and persistence
    Cookies.set('datahub-lang', lang, { expires: 365 }); // expires in 1 year

    // You could also set html lang attribute here if needed
    document.querySelector('html')?.setAttribute('lang', lang);

    return lang;
}

/*
 * i18NEXT COMPATIBILITY FIX FOR HYPHENATED LANGUAGE CODES
 *
 * ISSUE:
 * - Backend correctly sends 'pt-br' and pt-br.json file exists
 * - However, i18next has issues handling hyphenated language codes like 'pt-br'
 * - Translations load but i18next cannot properly use them internally
 *
 * SOLUTION:
 * - Create an "illusion" for i18next compatibility
 * - Load pt-br.json file normally
 * - But register translations under 'ptBr' (camelCase) for i18next
 * - This bypasses i18next's hyphenated language code issues
 */

/**
 * Maps hyphenated language codes to i18next-compatible format
 * This is specifically to handle i18next's issues with hyphenated codes
 * @param lang The language code from the file/backend
 * @returns The i18next-compatible language code
 */
const mapToI18nextCompatibleFormat = (lang: string) => {
    switch (lang) {
        case 'pt-br':
            return 'ptBr'; // Convert to camelCase for i18next compatibility
        default:
            return lang; // Other languages work fine as-is
    }
};

/**
 * Asynchronously loads a language with i18next compatibility handling
 * @param lang The language code to load (e.g., 'pt-br' from backend)
 * @returns A promise resolving to the i18next-compatible language code
 */
export async function loadLanguageAsync(lang: string) {
    // Import new language file dynamically
    try {
        const messages = await import(/* webpackChunkName: "lang-[request]" */ `./lang/${lang}.json`);
        const translations = (messages.default || messages)[lang];

        // Convert to i18next-compatible format (pt-br → ptBr) to avoid hyphen issues
        const mappedLang = mapToI18nextCompatibleFormat(lang);

        if (translations) {
            console.log(`Loading ${lang} file → registering as ${mappedLang} for i18next compatibility`);
            i18n.addResourceBundle(mappedLang, 'translation', translations);
            loadedLanguages.push(mappedLang);
            return setI18nLanguage(mappedLang);
        }
        console.error(`No translations found for language: ${lang}`);
        return setI18nLanguage('en'); // Fallback to English
    } catch (error) {
        console.error(`Could not load language: ${lang}`, error);
        return setI18nLanguage('en'); // Fallback to English
    }
}

export default i18n;
